/// <reference types="vite/client" />

import { getBrowserWindow, getNodeProcess } from "../lib/runtime";

type Environment = "development" | "dev" | "preprod" | "production";

const ENVIRONMENTS = [
  "development",
  "dev",
  "preprod",
  "production",
] as const;

function parseEnvironment(raw: string | undefined): Environment | undefined {
  for (const env of ENVIRONMENTS) {
    if (raw === env) return env;
  }
  return undefined;
}

interface SiteUrls {
  platform: Record<Environment, string>;
  devdocs: Record<Environment, string>;
}

const urls: SiteUrls = {
  platform: {
    development: "http://localhost:8080",
    dev: "https://platform-dev.youversion.com",
    preprod: "https://platform-preprod.youversion.com",
    production: "https://platform.youversion.com",
  },
  devdocs: {
    development: "http://localhost:3000",
    dev: "https://developers-dev.youversion.com",
    preprod: "https://developers-preprod.youversion.com",
    production: "https://developers.youversion.com",
  },
};

// When rendering on the server, prefer local URLs during development builds
// Note: This variable is kept for potential future use but currently unused
// const isServerDev =
//   typeof window === "undefined" && process.env.NODE_ENV === "development";

/**
 * Determines the current environment based on:
 * 1. Explicit environment variable (VITE_APP_ENV) - highest priority
 * 2. Client-side hostname detection - fallback
 * 3. SSR in a development server (NODE_ENV=development)
 * 4. Unrecognized host or no window: the build env
 *    (VITE_PLATFORM_PORTAL_ENVIRONMENT or ZUDOKU_PUBLIC_ENVIRONMENT),
 *    "preprod" -> preprod, "development" -> dev
 * 5. Defaults to production
 */
const getEnvironment = (win: Window | null = getBrowserWindow()): Environment => {
  const fromVite = parseEnvironment(import.meta.env.VITE_APP_ENV);
  if (fromVite) return fromVite;

  const fromProcess = parseEnvironment(getNodeProcess()?.env.VITE_APP_ENV);
  if (fromProcess) return fromProcess;

  const host = win?.location.hostname;
  if (host) {
    if (
      host === "localhost" ||
      host === "127.0.0.1" ||
      host.endsWith(".local")
    ) {
      return "development";
    }

    if (
      host === "platform-dev.youversion.com" ||
      host === "developers-dev.youversion.com" ||
      host.includes("-dev.youversion.com")
    ) {
      return "dev";
    }

    if (
      host.includes("-preprod.youversion.com") ||
      host.includes("preprod")
    ) {
      return "preprod";
    }

    if (
      host === "platform.youversion.com" ||
      host === "developers.youversion.com"
    ) {
      return "production";
    }
  } else if (
    !win &&
    (import.meta.env.DEV || getNodeProcess()?.env.NODE_ENV === "development")
  ) {
    return "development";
  }

  // Reached on PR preview hosts and when prerendering docs (no window); only
  // the build env names the environment there. dev-deploy.yml builds the
  // -dev hosts with "development" (kept for feature gating), and the browser
  // on those hosts resolves "dev", so a deployed "development" build means dev.
  // Local dev servers returned "development" above.
  const buildEnvs = BUILD_ENV_KEYS.map(
    (key) => import.meta.env[key] ?? getNodeProcess()?.env[key]
  );
  if (buildEnvs.includes("preprod")) return "preprod";
  if (buildEnvs.includes("development")) return "dev";

  return "production";
};

const BUILD_ENV_KEYS = [
  "VITE_PLATFORM_PORTAL_ENVIRONMENT",
  "ZUDOKU_PUBLIC_ENVIRONMENT",
] as const;

export const getPlatformUrl = (win?: Window | null): string => {
  const env = getEnvironment(win);
  return urls.platform[env];
};

export const getDevdocsUrl = (win?: Window | null): string => {
  const env = getEnvironment(win);
  return urls.devdocs[env];
};

/**
 * Returns the current detected environment.
 * Useful for debugging and conditional logic.
 */
export const getCurrentEnvironment = (): Environment => {
  return getEnvironment();
};

export const getCurrentSiteUrl = (): string => {
  const win = getBrowserWindow();
  if (!win) {
    return urls.devdocs[getEnvironment()];
  }
  return win.location.origin;
};


export default urls;
